import React, { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  CircularProgress, 
  Chip, 
  Button, 
  Alert, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/apiService';
import PageContainer from '../components/PageContainer';
import { twClasses } from '../styles/tailwind-utils';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { CalendarMonth, Email as EmailIcon, Group } from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchDailyEmailSummaryV2, DateRange } from '../services/analyticsService';
import DateRangePicker from '../components/analytics/DateRangePicker';
import EmailPreviewModal from '../components/EmailPreviewModal';
import ContactListModal from '../components/ContactListModal';

interface InvitationResponse {
  message?: string;
  client: {
    id: number;
    agency_name: string;
    url?: string;
    active?: boolean;
  };
  role: string;
}

interface Email {
  id: number;
  name: string;
  subject: string;
  segment_name: string;
  scheduled_date: string;
}

interface SegmentCount {
  name: string;
  count: number;
  color: string;
}

interface SegmentCountResponse {
  id: number;
  name: string;
  count: number;
}

interface SegmentCountWithId extends SegmentCount {
  id: number;
}

// Format date for display
const formatDate = (dateString: string) => {
  return format(new Date(dateString), 'MMMM d, yyyy');
};

// Get color for segment
const getSegmentColor = (segment_name: string) => {
  switch (segment_name?.toLowerCase()) {
    case 'newsletter':
      return '#3b82f6'; // blue
    case 'luxury':
      return '#9333ea'; // purple
    case 'romance':
      return '#ec4899'; // pink
    case 'family':
      return '#22c55e'; // green
    case 'ultralux':
      return '#f59e0b'; // amber
    case 'unsubscribed':
      return '#9ca3af'; // light grey
    default:
      return '#3174ad'; // default blue
  }
};

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNoAgencyAlert] = useState(false);
  const [checkingAgency] = useState(false);
  
  // Dashboard data state
  const [upcomingEmails, setUpcomingEmails] = useState<Email[]>([]);
  const [segmentCounts, setSegmentCounts] = useState<SegmentCountWithId[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  // Note: emailOpens and opensLoading removed - using engagementData instead
  
  // Email Engagement state
  const [engagementData, setEngagementData] = useState<any>(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({ date_range: 'last_30_days' });
  const [currentDateRange, setCurrentDateRange] = useState<{startDate: string, endDate: string}>({startDate: '', endDate: ''});
  const [showSends, setShowSends] = useState(true);
  const [showOpens, setShowOpens] = useState(true);
  const [showClicks, setShowClicks] = useState(true);
  
  const navigate = useNavigate();
  const { user, selectedClientId, setSelectedClientId, isAdmin } = useUser();
  const auth = useUser();
  
  // Email preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<Email | null>(null);

  // Contact list modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedSegmentForModal, setSelectedSegmentForModal] = useState<SegmentCountWithId | null>(null);

  // Initialize auth and load user data
  useEffect(() => {
    // Wait for UserContext to finish its initial session fetch before deciding
    // whether to redirect — otherwise we race the cookie-auth bootstrap and
    // kick logged-in users back to the landing page.
    if (auth.isLoading) {
      return;
    }

    const initializeAuth = async () => {
      try {
        setError(null);
        setLoading(true);

        if (!auth.isAuthenticated) {
          navigate('/', { replace: true });
          return;
        }

        // Set first client as selected if none selected
        if (user?.client_assignments && !selectedClientId) {
          const assignments = user.client_assignments;
          if (assignments.length > 0) {
            const firstAssignment = assignments[0];
            setSelectedClientId(firstAssignment.client_id);
          }
        }
      } catch (error) {
        console.error('[Dashboard] Auth initialization error:', error);
        setError('Failed to initialize dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [auth.isLoading, auth.isAuthenticated, user]);

  // Fetch upcoming emails
  const fetchUpcomingEmails = useCallback(async () => {
    if (!selectedClientId) return;
    
    try {
      setEmailsLoading(true);
      const response = await apiService.get('/api/emails', { 
        params: { 
          client_id: selectedClientId,
          include_drafts: false,
          future_only: true,
          limit: 5
        } 
      });
      
      setUpcomingEmails(response.data as Email[]);
    } catch (error) {
      console.error('Error fetching upcoming emails:', error);
    } finally {
      setEmailsLoading(false);
    }
  }, [selectedClientId]);

  // Contact modal handlers
  const handleOpenContactModal = (segment: SegmentCountWithId) => {
    setSelectedSegmentForModal(segment);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedSegmentForModal(null);
  };

  // Fetch segment counts
  const fetchSegmentCounts = useCallback(async () => {
    if (!selectedClientId) return;
    
    try {
      setSegmentsLoading(true);
      const response = await apiService.get<SegmentCountResponse[]>('/api/segments/counts', {
        params: {
          client_id: selectedClientId
        }
      });
      
      // Map the response to our format
      const counts: SegmentCountWithId[] = response.data
        .filter((seg) => seg.count > 0)
        .map((seg) => ({
          id: seg.id,
          name: seg.name,
          count: seg.count,
          color: getSegmentColor(seg.name)
        }));
      
      // Sort segments by count in descending order
      counts.sort((a, b) => b.count - a.count);
      
      setSegmentCounts(counts);
    } catch (error) {
      console.error('Could not fetch segment counts:', error);
    } finally {
      setSegmentsLoading(false);
    }
  }, [selectedClientId]);
  
  // Note: fetchEmailOpens removed - we now use engagementData for both chart and performance summary
  // This eliminates duplicate API calls
  
  // Fetch email engagement data
  const fetchEngagementData = useCallback(async () => {
    if (!selectedClientId) return;
    
    try {
      setEngagementLoading(true);
      
      // Calculate date range
      let startDate: string | undefined;
      let endDate: string | undefined;
      
      if (dateRange.date_range === 'custom' && dateRange.startDate && dateRange.endDate) {
        startDate = dateRange.startDate.toISOString().split('T')[0];
        endDate = dateRange.endDate.toISOString().split('T')[0];
      } else {
        // Calculate date range based on selection
        const end = new Date();
        const start = new Date();
        
        switch (dateRange.date_range) {
          case 'last_7_days':
            start.setDate(end.getDate() - 7);
            break;
          case 'last_30_days':
            start.setDate(end.getDate() - 30);
            break;
          case 'last_90_days':
            start.setDate(end.getDate() - 90);
            break;
          case 'last_12_months':
            start.setMonth(end.getMonth() - 12);
            break;
          default:
            start.setDate(end.getDate() - 30);
        }
        
        startDate = start.toISOString().split('T')[0];
        endDate = end.toISOString().split('T')[0];
        
        console.log('Date range calculation:', {
          dateRange: dateRange.date_range,
          startDate,
          endDate,
          daysDiff: Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))
        });
      }
      
      // Store current date range for chart rendering
      setCurrentDateRange({ startDate, endDate });
      
      const response = await fetchDailyEmailSummaryV2(
        selectedClientId,
        startDate,
        endDate,
        false // includeSpam = false (use filtered data)
      );
      
      console.log('Dashboard fetchEngagementData response:', {
        selectedClientId,
        startDate,
        endDate,
        dateRange,
        response,
        time_series_length: response?.time_series?.length,
        sample_record: response?.time_series?.[0],
        all_records: response?.time_series?.map(r => ({
          date: r.date,
          sends: r.total_sends,
          opens: r.total_opens,
          clicks: r.total_clicks
        }))
      });
      
      if (response?.time_series) {
        // Calculate days diff to determine if we need to aggregate by month
        const daysDiff = Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
        
        let chartData = [];
        
        if (daysDiff > 90) {
          // Aggregate by month for periods > 90 days
          const monthlyData = new Map();
          
          response.time_series
            .filter(day => day && day.date)
            .forEach(day => {
              const date = new Date(day.date);
              const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
              
              console.log('Monthly aggregation:', {
                original_date: day.date,
                parsed_date: date.toISOString(),
                getFullYear: date.getFullYear(),
                getMonth: date.getMonth(),
                getMonth_plus_1: date.getMonth() + 1,
                month_key: monthKey,
                sends: day.total_sends,
                opens: day.total_opens,
                clicks: day.total_clicks,
                expected_month: day.date.startsWith('2025-02') ? 'February' : 'Other'
              });
              
              if (!monthlyData.has(monthKey)) {
                monthlyData.set(monthKey, {
                  sends: 0,
                  opens: 0,
                  clicks: 0
                });
              }
              
              const existing = monthlyData.get(monthKey);
              existing.sends += Number(day.total_sends) || 0;
              existing.opens += Number(day.total_opens) || 0;
              existing.clicks += Number(day.total_clicks) || 0;
            });
          
          // Generate complete monthly range with zero values for missing months
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          const completeMonthlyData = [];
          
          // Start from the first day of the start month
          let currentMonth = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), 1);
          // End at the last day of the end month
          const endMonth = new Date(endDateObj.getFullYear(), endDateObj.getMonth() + 1, 0);
          
          while (currentMonth <= endMonth) {
            const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-01`;
            const existingData = monthlyData.get(monthKey);
            
            completeMonthlyData.push({
              date: monthKey,
              sends: existingData?.sends || 0,
              opens: existingData?.opens || 0,
              clicks: existingData?.clicks || 0
            });
            
            // Move to next month
            currentMonth.setMonth(currentMonth.getMonth() + 1);
          }
          
          chartData = completeMonthlyData;
          
          console.log('Monthly aggregation result:', {
            monthlyData: Array.from(monthlyData.entries()),
            chartData,
            daysDiff
          });
          
        } else if (daysDiff > 30) {
          // Aggregate by week for periods 31-90 days (week ending Sunday)
          const weeklyData = new Map();
          
          response.time_series
            .filter(day => day && day.date)
            .forEach(day => {
              const date = new Date(day.date);
              // Find the Sunday on or after this date
              const daysUntilSunday = (7 - date.getDay()) % 7;
              const sunday = new Date(date);
              sunday.setDate(date.getDate() + daysUntilSunday);
              const weekKey = sunday.toISOString().split('T')[0];
              
              if (!weeklyData.has(weekKey)) {
                weeklyData.set(weekKey, {
                  sends: 0,
                  opens: 0,
                  clicks: 0
                });
              }
              
              const existing = weeklyData.get(weekKey);
              existing.sends += Number(day.total_sends) || 0;
              existing.opens += Number(day.total_opens) || 0;
              existing.clicks += Number(day.total_clicks) || 0;
            });
          
          // Generate complete weekly range with zero values for missing weeks
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          const completeWeeklyData = [];
          
          // Find the first Sunday on or after start date
          let currentWeek = new Date(startDateObj);
          while (currentWeek.getDay() !== 0) {
            currentWeek.setDate(currentWeek.getDate() + 1);
          }
          
          // Generate all weeks until we pass the end date
          while (currentWeek <= endDateObj) {
            const weekKey = currentWeek.toISOString().split('T')[0];
            const existingData = weeklyData.get(weekKey);
            
            completeWeeklyData.push({
              date: weekKey,
              sends: existingData?.sends || 0,
              opens: existingData?.opens || 0,
              clicks: existingData?.clicks || 0
            });
            
            // Move to next week
            currentWeek.setDate(currentWeek.getDate() + 7);
          }
          
          chartData = completeWeeklyData;
          
        } else {
          // Keep daily granularity for periods <= 30 days
          const dataMap = new Map();
          response.time_series
            .filter(day => day && day.date)
            .forEach(day => {
              dataMap.set(day.date, {
                sends: Number(day.total_sends) || 0,
                opens: Number(day.total_opens) || 0,
                clicks: Number(day.total_clicks) || 0
              });
            });
          
          // Generate complete date range
          const currentDate = new Date(startDate);
          const endDateObj = new Date(endDate);
          
          while (currentDate <= endDateObj) {
            const dateStr = currentDate.toISOString().split('T')[0];
            const existingData = dataMap.get(dateStr);
            
            chartData.push({
              date: dateStr,
              sends: existingData?.sends || 0,
              opens: existingData?.opens || 0,
              clicks: existingData?.clicks || 0
            });
            
            currentDate.setDate(currentDate.getDate() + 1);
          }
        }
        
        console.log('Dashboard chartData:', {
          original_length: response.time_series.length,
          filtered_length: chartData.length,
          sample_chart_data: chartData[0],
          clicks_sample: chartData.slice(0, 3).map(d => ({ date: d.date, clicks: d.clicks })),
          aggregation_type: daysDiff > 90 ? 'monthly' : (daysDiff > 30 ? 'weekly' : 'daily'),
          date_range: { startDate, endDate },
          all_chart_data: chartData
        });
        
        setEngagementData({
          summary: response.summary,
          chartData
        });
      }
    } catch (error) {
      console.error('Error fetching engagement data:', error);
    } finally {
      setEngagementLoading(false);
    }
  }, [selectedClientId, dateRange]);
  
  // Fetch dashboard data when client is selected
  useEffect(() => {
    if (selectedClientId && !loading) {
      fetchUpcomingEmails();
      fetchSegmentCounts();
      // Note: fetchEmailOpens removed - data comes from fetchEngagementData
    }
  }, [selectedClientId, loading, fetchUpcomingEmails, fetchSegmentCounts]);

  // Fetch engagement data when client or date range changes
  useEffect(() => {
    console.log('Dashboard useEffect triggered:', {
      selectedClientId,
      loading,
      dateRange,
      shouldFetch: selectedClientId && !loading,
      dateRangeString: JSON.stringify(dateRange)
    });
    if (selectedClientId && !loading) {
      console.log('Calling fetchEngagementData...');
      fetchEngagementData();
    }
  }, [selectedClientId, dateRange, fetchEngagementData]);

  // Handle opening email preview modal
  const handleOpenEmailPreview = async (email: Email) => {
    try {
      // Fetch full email details including HTML content
      const response = await apiService.get<Email>(`/api/emails/${email.id}`);
      if (response.data) {
        setPreviewEmail(response.data);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching email details:', error);
      // Fallback to basic email info if API call fails
      setPreviewEmail(email);
      setPreviewModalOpen(true);
    }
  };
  
  // Handle closing email preview modal
  const handleCloseEmailPreview = () => {
    setPreviewModalOpen(false);
    setPreviewEmail(null);
  };

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <CircularProgress />
            <Typography variant="h6" className="mt-4">
              Loading your dashboard...
            </Typography>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-screen">
          <Alert severity="error" className="max-w-md">
            {error}
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              Retry
            </Button>
          </Alert>
        </div>
      </PageContainer>
    );
  }

  if (!selectedClientId && checkingAgency) {
    return (
      <PageContainer>
        <div className={twClasses.loadingContainer}>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-main"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Dashboard
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Show no agency alert */}
          {showNoAgencyAlert && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You need to select an agency to access certain features.
              </Alert>
            </Grid>
          )}

          {/* Upcoming Emails Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarMonth color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Upcoming Email Campaigns
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {emailsLoading ? (
                <Box>
                  {[...Array(3)].map((_, i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="60%" height={30} />
                      <Skeleton variant="text" width="40%" height={24} />
                      <Skeleton variant="text" width="30%" height={24} />
                    </Box>
                  ))}
                </Box>
              ) : upcomingEmails.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Segment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcomingEmails.map((email) => (
                        <TableRow 
                          key={email.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{formatDate(email.scheduled_date)}</TableCell>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEmailPreview(email);
                              }}
                              sx={{ 
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                p: 0,
                                minWidth: 'auto',
                                color: 'primary.main',
                                '&:hover': {
                                  textDecoration: 'underline',
                                  backgroundColor: 'transparent'
                                }
                              }}
                            >
                              {email.name}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={email.segment_name ? email.segment_name.charAt(0).toUpperCase() + email.segment_name.slice(1) : 'No Segment'}
                              size="small"
                              sx={{
                                backgroundColor: getSegmentColor(email.segment_name),
                                color: 'white',
                                height: '24px',
                                minWidth: '80px',
                                '& .MuiChip-label': {
                                  px: 1,
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'visible'
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" py={3}>
                  <Typography color="textSecondary">
                    No upcoming email campaigns scheduled.
                  </Typography>
                  <Button 
                    component={RouterLink}
                    to="/app/emails"
                    variant="outlined"
                    sx={{ mt: 2 }}
                  >
                    View All
                  </Button>
                </Box>
              )}
              
              {upcomingEmails.length > 0 && (
                <Box textAlign="right" mt={2}>
                  <Button 
                    component={RouterLink}
                    to="/app/emails"
                    size="small"
                  >
                    View All
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Segments Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Group color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Audience Segments
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {segmentsLoading ? (
                <Box>
                  {[...Array(4)].map((_, i) => (
                    <Box key={i} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                      <Skeleton variant="text" width="40%" height={30} />
                      <Skeleton variant="text" width="20%" height={30} />
                    </Box>
                  ))}
                </Box>
              ) : segmentCounts.length > 0 ? (
                <Box>
                  {segmentCounts.map((segment) => (
                    <Box 
                      key={segment.name} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 2,
                        pb: 1,
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          borderRadius: '4px',
                          padding: '8px'
                        }
                      }}
                      onClick={() => handleOpenContactModal(segment)}
                    >
                      <Box display="flex" alignItems="center">
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: segment.color,
                            mr: 1 
                          }} 
                        />
                        <Typography>
                          {segment.name.charAt(0).toUpperCase() + segment.name.slice(1)}
                        </Typography>
                      </Box>
                      <Typography fontWeight="bold" sx={{ color: 'primary.main' }}>
                        {segment.count.toLocaleString()} contacts
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" py={3}>
                  <Typography color="textSecondary">
                    No segments with contacts found.
                  </Typography>
                  <Button 
                    component={RouterLink}
                    to="/app/contacts"
                    variant="outlined"
                    sx={{ mt: 2 }}
                  >
                    Manage Contacts
                  </Button>
                </Box>
              )}
              
              {segmentCounts.length > 0 && (
                <Box textAlign="right" mt={2}>
                  <Button 
                    component={RouterLink}
                    to="/app/contacts"
                    size="small"
                  >
                    View All
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Email Engagement Chart */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <EmailIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Email Engagement
                  </Typography>
                </Box>
                <DateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {/* Toggle Controls */}
              <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={showSends}
                      onChange={(e) => setShowSends(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Sends"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOpens}
                      onChange={(e) => setShowOpens(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Opens"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showClicks}
                      onChange={(e) => setShowClicks(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Clicks"
                />
              </Box>
              
              <Box sx={{ height: 300 }}>
                {engagementLoading ? (
                  <Skeleton variant="rectangular" height={300} />
                ) : engagementData?.chartData && engagementData.chartData.length > 0 ? (
                  (() => {
                    const { startDate, endDate } = currentDateRange;
                    const daysDiff = startDate && endDate 
                      ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))
                      : 0;
                    
                    const formatDate = (dateStr: string) => {
                      // Parse the date string to avoid timezone issues
                      const [year, month, day] = dateStr.split('-').map(Number);
                      const date = new Date(year, month - 1, day); // month is 0-indexed
                      
                      if (daysDiff > 90) {
                        return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
                      } else if (daysDiff > 30) {
                        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                      } else {
                        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                      }
                    };
                    
                    // Use bar chart for weekly and monthly aggregations
                    if (daysDiff > 30) {
                      const barData = engagementData.chartData.map(day => ({
                        date: formatDate(day.date),
                        ...(showSends && { sends: Math.max(0, Number(day.sends) || 0) }),
                        ...(showOpens && { opens: Math.max(0, Number(day.opens) || 0) }),
                        ...(showClicks && { clicks: Math.max(0, Number(day.clicks) || 0) })
                      }));
                      
                      return <ResponsiveBar
                    data={barData}
                    keys={[
                      ...(showSends ? ['sends'] : []),
                      ...(showOpens ? ['opens'] : []),
                      ...(showClicks ? ['clicks'] : [])
                    ]}
                    indexBy="date"
                    margin={{ top: 30, right: 40, bottom: 70, left: 70 }}
                    padding={0.3}
                    groupMode="grouped"
                    colors={({ id }) => {
                      if (id === 'sends') return 'hsl(210, 70%, 50%)';
                      if (id === 'opens') return 'hsl(120, 70%, 50%)';
                      if (id === 'clicks') return 'hsl(30, 70%, 50%)';
                      return 'hsl(0, 0%, 50%)';
                    }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 12,
                      tickRotation: -45,
                      legend: 'Date',
                      legendOffset: 55,
                      legendPosition: 'middle'
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 8,
                      tickRotation: 0,
                      legend: 'Count',
                      legendOffset: -50,
                      legendPosition: 'middle'
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    animate={false}
                    tooltip={({ id, value, indexValue }) => (
                      <div className="bg-white px-3 py-2 rounded shadow text-gray-800 text-sm font-medium border border-gray-200">
                        <div className="font-semibold mb-1">
                          {daysDiff > 90 ? indexValue : (daysDiff > 30 ? `Week ending ${indexValue}` : indexValue)}
                        </div>
                        <div className="capitalize">{id}: {value.toLocaleString()}</div>
                      </div>
                    )}
                  />;
                    }
                    
                    // Use line chart for daily data
                    return <ResponsiveLine
                    data={[
                        ...(showSends ? [{
                          id: 'sends',
                          color: 'hsl(210, 70%, 50%)',
                          data: engagementData.chartData
                            .filter(day => day && day.date && !isNaN(day.sends) && day.sends !== null && day.sends !== undefined)
                            .map(day => ({
                              x: formatDate(day.date),
                              y: Math.max(0, Number(day.sends) || 0)
                            }))
                        }] : []),
                        ...(showOpens ? [{
                          id: 'opens',
                          color: 'hsl(120, 70%, 50%)',
                          data: engagementData.chartData
                            .filter(day => day && day.date && !isNaN(day.opens) && day.opens !== null && day.opens !== undefined)
                            .map(day => ({
                              x: formatDate(day.date),
                              y: Math.max(0, Number(day.opens) || 0)
                            }))
                        }] : []),
                        ...(showClicks ? [{
                          id: 'clicks',
                          color: 'hsl(30, 70%, 50%)',
                          data: engagementData.chartData
                            .filter(day => day && day.date && !isNaN(day.clicks) && day.clicks !== null && day.clicks !== undefined)
                            .map(day => ({
                              x: formatDate(day.date),
                              y: Math.max(0, Number(day.clicks) || 0)
                            }))
                        }] : [])
                      ]}
                    margin={{ top: 30, right: 40, bottom: 70, left: 70 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto',
                      stacked: false,
                      reverse: false
                    }}
                    curve="monotoneX"
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 12,
                      tickRotation: -45,
                      legend: 'Date',
                      legendOffset: 55,
                      legendPosition: 'middle',
                      tickValues: (() => {
                        const { startDate, endDate } = currentDateRange;
                        if (!startDate || !endDate) return [];
                        
                        const daysDiff = Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
                        
                        if (daysDiff <= 30) {
                          // Daily marks for 30 days or less
                          return engagementData.chartData
                            .filter((_: any, i: number) => i % Math.max(1, Math.ceil(engagementData.chartData.length / 10)) === 0)
                            .map((day: any) => new Date(day.date).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric'
                            }));
                        } else if (daysDiff <= 90) {
                          // Weekly marks (week ending Sunday) for 31-90 days
                          const weeklyTicks = [];
                          const start = new Date(startDate);
                          const end = new Date(endDate);
                          
                          // Find first Sunday on or after start date
                          let current = new Date(start);
                          while (current.getDay() !== 0) {
                            current.setDate(current.getDate() + 1);
                          }
                          
                          while (current <= end) {
                            weeklyTicks.push(new Date(current).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric'
                            }));
                            current.setDate(current.getDate() + 7);
                          }
                          
                          return weeklyTicks;
                        } else {
                          // Monthly marks for >90 days - just month names
                          const monthlyTicks = [];
                          const start = new Date(startDate);
                          const end = new Date(endDate);
                          
                          let current = new Date(start.getFullYear(), start.getMonth(), 1);
                          while (current <= end) {
                            monthlyTicks.push(current.toLocaleDateString(undefined, {
                              month: 'short'
                            }));
                            current.setMonth(current.getMonth() + 1);
                          }
                          
                          return monthlyTicks;
                        }
                      })()
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 8,
                      tickRotation: 0,
                      legend: 'Count',
                      legendOffset: -50,
                      legendPosition: 'middle'
                    }}
                    enableGridX={false}
                    colors={{ scheme: 'category10' }}
                    pointSize={8}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enableArea={true}
                    areaOpacity={0.15}
                    enableSlices="x"
                    useMesh={true}
                    animate={false}
                    sliceTooltip={({ slice }) => {
                      const daysDiff = currentDateRange.startDate && currentDateRange.endDate
                        ? Math.ceil((new Date(currentDateRange.endDate).getTime() - new Date(currentDateRange.startDate).getTime()) / (1000 * 60 * 60 * 24))
                        : 0;
                      
                      // Sort points in the desired order: sends, opens, clicks
                      const orderedPoints = [...slice.points].sort((a, b) => {
                        const order = { 'sends': 1, 'opens': 2, 'clicks': 3 };
                        return order[a.serieId as string] - order[b.serieId as string];
                      });
                      
                      // Format date based on aggregation period
                      let dateLabel = '';
                      if (daysDiff > 90) {
                        // Monthly - show month and year
                        dateLabel = slice.points[0].data.xFormatted;
                      } else if (daysDiff > 30) {
                        // Weekly - show "Week ending [date]"
                        dateLabel = 'Week ending ' + slice.points[0].data.xFormatted;
                      } else {
                        // Daily - show full date
                        dateLabel = new Date(slice.points[0].data.xFormatted).toLocaleDateString(undefined, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      }
                      
                      return (
                        <div className="bg-white px-3 py-2 rounded shadow text-gray-800 text-sm font-medium border border-gray-200">
                          <div className="font-semibold mb-1">
                            {dateLabel}
                          </div>
                          {orderedPoints.map((point) => (
                            <div key={point.id} className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: point.serieColor }}
                              />
                              <span className="capitalize">{point.serieId}: {point.data.yFormatted}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />;
                  })()
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="textSecondary">
                      No engagement data available for the selected period.
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box textAlign="right" mt={2}>
                <Button 
                  component={RouterLink}
                  to="/app/analytics"
                  size="small"
                >
                  View All
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Performance Summary Section */}
          <Grid item xs={12} md={12}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" mb={2}>
                Performance Summary ({dateRange.date_range === 'last_30_days' ? '30 days' : dateRange.date_range === 'last_7_days' ? '7 days' : dateRange.date_range === 'last_90_days' ? '90 days' : 'selected period'})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {engagementLoading ? (
                <Box>
                  {[...Array(3)].map((_, i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                  ))}
                </Box>
              ) : engagementData?.summary ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" color="textSecondary">Total Sends</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {engagementData.summary.total_sends?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" color="textSecondary">Total Opens</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {engagementData.summary.total_opens?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Total Clicks</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {engagementData.summary.total_clicks?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box textAlign="center" py={3}>
                  <Typography color="textSecondary">
                    No performance data available.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={handleCloseEmailPreview}
        email={previewEmail ? {
          name: previewEmail.name,
          subject: previewEmail.subject,
          html: previewEmail.html || '',
          scheduled_date: previewEmail.scheduled_date,
          agency_name: '', // We don't have agency name in the Email interface
          segment_name: previewEmail.segment_name,
          existing_tags: [] // We don't have tags in the Email interface
        } : null}
      />

      {/* Contact List Modal */}
      {selectedSegmentForModal && (
        <ContactListModal
          key={`segment-contact-modal-${selectedClientId}-${selectedSegmentForModal.id}`}
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          title="Contacts"
          subtitle={`${selectedSegmentForModal.name} segment contacts`}
          dataSource="segment"
          config={{
            segmentId: selectedSegmentForModal.id,
            segmentName: selectedSegmentForModal.name,
            clientId: selectedClientId!,
            dateRange: {
              date_range: 'all_time',
              startDate: undefined,
              endDate: undefined
            }
          }}
        />
      )}
    </PageContainer>
  );
};

export default Dashboard;
